import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { authApi } from '@/api/client';
import { AUTH_ME_QUERY_KEY, useAuth } from '@/lib/auth';
import { AUTH_LINK_CLASS, AuthError, AuthShell, AuthSubmit } from '@os/components/AuthShell';

/**
 * L'écran qu'un compte non confirmé voit À LA PLACE de l'application.
 *
 * Depuis que `ConfirmedEmailGuard` est une garde globale côté back, tout ce qui
 * suit le login répond 403 tant que l'adresse n'est pas prouvée. Sans cet
 * écran, le front afficherait sa coquille habituelle et la remplirait d'erreurs
 * — un mur blanc où chaque panneau échoue pour une raison qu'aucun d'eux ne
 * nomme. Mieux vaut une porte fermée avec sa clé posée dessus.
 *
 * TROIS GESTES, ET RIEN D'AUTRE, parce que ce sont exactement les trois que le
 * back laisse encore passer (`@AllowsUnconfirmedEmail()`) : se faire renvoyer le
 * courrier, dire qu'on a cliqué, partir. Ajouter un quatrième bouton ici
 * promettrait une route que la garde refuse.
 *
 * « J'ai confirmé » ne fait qu'invalider `/auth/me`. La confirmation se fait
 * dans un AUTRE onglet — souvent sur le téléphone — et rien ne revient prévenir
 * cet onglet-ci : c'est le re-fetch qui apporte le `emailConfirmed: true`, et
 * l'application s'ouvre d'elle-même puisque c'est `ProtectedRoute` qui lit ce
 * champ.
 */
export function ConfirmEmailGate() {
  const { t } = useTranslation('auth');
  const { user, logout } = useAuth();
  const queryClient = useQueryClient();

  const [resend, setResend] = useState<'idle' | 'sending' | 'sent' | 'throttled' | 'error'>('idle');
  const [rechecking, setRechecking] = useState(false);

  const handleResend = () => {
    setResend('sending');
    authApi
      .resendVerification()
      .then(() => setResend('sent'))
      .catch((err: unknown) => {
        // 3 renvois par heure côté serveur : un 429 est une réponse normale,
        // pas une panne, et se dit avec ses mots à lui.
        const status = (err as { response?: { status?: number } })?.response?.status;
        setResend(status === 429 ? 'throttled' : 'error');
      });
  };

  const handleRecheck = () => {
    setRechecking(true);
    void queryClient
      .invalidateQueries({ queryKey: AUTH_ME_QUERY_KEY })
      .finally(() => setRechecking(false));
  };

  return (
    <AuthShell
      title={t('confirmGate.title')}
      subtitle={t('confirmGate.subtitle')}
      footer={
        <button
          type="button"
          data-testid="button-gate-logout"
          onClick={logout}
          className="text-ink-2 underline decoration-ink-3/56 underline-offset-4 hover:decoration-ink-2"
        >
          {t('confirmGate.signOut')}
        </button>
      }
    >
      {resend === 'error' && <AuthError>{t('checkEmail.resendFailed')}</AuthError>}

      <div className="space-y-4">
        {user?.email && (
          <p data-testid="gate-email-address" className="text-[0.88rem] leading-relaxed text-ink-2">
            {t('checkEmail.sentTo')} <strong className="font-medium text-ink">{user.email}</strong>
          </p>
        )}

        <p className="text-[0.82rem] leading-relaxed text-ink-3">{t('checkEmail.expiry')}</p>

        {/*
          `AuthSubmit` est un `type="submit"` : pas de formulaire ici, donc on lui
          en donne un plutôt que d'ajouter un second mode au composant partagé.
          Même geste que `CheckEmailPage`.
        */}
        <form onSubmit={(e) => { e.preventDefault(); handleRecheck(); }}>
          <AuthSubmit loading={rechecking} loadingLabel={t('confirmGate.rechecking')}>
            {t('confirmGate.recheck')}
          </AuthSubmit>
        </form>

        <div className="flex flex-wrap items-center gap-2 pt-1">
          {resend === 'sent' ? (
            <span role="status" data-testid="gate-resent" className="text-[0.82rem] text-ink-2">
              {t('checkEmail.resent')}
            </span>
          ) : (
            <>
              <span className="text-[0.82rem] text-ink-3">{t('checkEmail.notReceived')}</span>
              <button
                type="button"
                data-testid="button-gate-resend"
                onClick={handleResend}
                disabled={resend === 'sending'}
                className={`text-[0.82rem] ${AUTH_LINK_CLASS} disabled:opacity-60`}
              >
                {resend === 'sending' ? t('checkEmail.resending') : t('checkEmail.resend')}
              </button>
            </>
          )}
          {resend === 'throttled' && (
            <span role="status" className="text-[0.82rem] text-ink-3">
              {t('checkEmail.resendThrottled')}
            </span>
          )}
        </div>
      </div>
    </AuthShell>
  );
}
